// src/Dashboard/pages/user/Galleries/components/GalleryCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { FaEye, FaPause, FaPen, FaPlay, FaTrash } from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../services/albumPhotoServices";

import {
  FALLBACK_IMAGE,
  getCoverPhoto,
  onImgError,
  sortPhotos,
  stripHtml,
} from "../utils/galleriesUtils";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export default function GalleryCard({ gallery, isManager, onEdit, onDelete }) {
  const navigate = useNavigate();

  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [purify, setPurify] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {}
    })();
  }, []);

  const orderedPhotos = useMemo(() => {
    return sortPhotos(gallery?.photos || []);
  }, [gallery?.photos]);

  const coverPhoto = useMemo(() => {
    return getCoverPhoto(orderedPhotos);
  }, [orderedPhotos]);

  const cover =
    toAbsolutePhotoUrl(coverPhoto?.photo_url || "") || FALLBACK_IMAGE;
  const hasMany = orderedPhotos.length > 1;

  const descText = useMemo(() => {
    return stripHtml(gallery?.description || "");
  }, [gallery?.description]);

  const shouldClamp = descText.length > 220;

  const safeHtml = useMemo(() => {
    const raw = gallery?.description || "";

    try {
      return purify ? purify.sanitize(raw) : raw;
    } catch {
      return raw;
    }
  }, [gallery?.description, purify]);

  const settings = {
    dots: true,
    infinite: orderedPhotos.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    lazyLoad: "ondemand",
    pauseOnHover: true,
    adaptiveHeight: false,
  };

  const handleOpenAlbum = () => {
    // Redirection conservée vers la page album.
    navigate(`/dashboard/galerie-photos/album/${gallery.id}`);
  };

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950">
      <div className="relative">
        {!isPlaying || !hasMany ? (
          <button
            type="button"
            onClick={handleOpenAlbum}
            className="block w-full bg-slate-100 text-left dark:bg-slate-900"
            title="Ouvrir l’album"
          >
            <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
              <img
                src={cover}
                alt={gallery?.titre || "Album"}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                loading="lazy"
                onError={onImgError}
              />
            </div>
          </button>
        ) : (
          <Slider {...settings}>
            {orderedPhotos.map((photo, index) => (
              <div
                key={`${gallery.id}-photo-${photo.id || index}`}
                className="w-full bg-slate-100 dark:bg-slate-900"
              >
                <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
                  <img
                    src={toAbsolutePhotoUrl(photo.photo_url) || FALLBACK_IMAGE}
                    alt={`${gallery?.titre || "Album"} - photo ${index + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={onImgError}
                  />
                </div>
              </div>
            ))}
          </Slider>
        )}

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          <span className="inline-flex rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white shadow-sm">
            {orderedPhotos.length} photo{orderedPhotos.length > 1 ? "s" : ""}
          </span>

          {!gallery?.is_active && (
            <span className="inline-flex rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
              Masqué
            </span>
          )}
        </div>

        {hasMany && (
          <button
            type="button"
            onClick={() => setIsPlaying((value) => !value)}
            title={isPlaying ? "Mettre en pause" : "Lancer le diaporama"}
            className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-white dark:bg-slate-950/90 dark:text-slate-200 dark:ring-slate-700"
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex-1">
          <h3 className="line-clamp-2 text-base font-bold text-slate-900 dark:text-white">
            {gallery?.titre || "Sans titre"}
          </h3>

          {safeHtml ? (
            <div className="relative mt-2">
              <div
                className={`prose prose-sm max-w-none text-slate-600 dark:prose-invert dark:text-slate-300 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 ${
                  expanded ? "" : "max-h-24 overflow-hidden"
                }`}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />

              {!expanded && shouldClamp && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />
              )}

              {shouldClamp && (
                <button
                  type="button"
                  onClick={() => setExpanded((value) => !value)}
                  className="mt-1 text-xs font-bold text-[#013E8E] hover:underline dark:text-indigo-300"
                >
                  {expanded ? "Réduire" : "Voir plus"}
                </button>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm italic text-slate-500 dark:text-slate-400">
              Pas de description.
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <button
            type="button"
            onClick={handleOpenAlbum}
            className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-800"
          >
            <FaEye />
            Voir l’album
          </button>

          {isManager && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onEdit}
                title="Modifier"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaPen />
              </button>

              <button
                type="button"
                onClick={onDelete}
                title="Supprimer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
